import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import {
  AlertTriangle, Clock, TriangleAlert, RefreshCw, CalendarDays, Inbox,
} from "lucide-react";
import { cn } from "../lib/utils";

function StatCard({ label, value, accent }) {
  return (
    <div className="border border-border bg-white p-5">
      <div className="label-caps text-muted-foreground">{label}</div>
      <div className={cn("font-heading text-3xl font-black mt-2", accent)}>{value}</div>
    </div>
  );
}

function FeedRow({ c, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`feed-row-${c.complaint_no}`}
      className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 border-b border-border hover:bg-secondary transition-colors"
    >
      <div className="min-w-0">
        <div className="font-mono text-xs font-semibold text-klein truncate">{c.complaint_no}</div>
        <div className="text-sm truncate">{c.customer_name} · {c.appliance_type}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <PriorityBadge priority={c.priority} />
        <StatusBadge status={c.status} />
      </div>
    </button>
  );
}

function Feed({ title, icon: Icon, items, accent, testId, navigate, empty }) {
  return (
    <div className="border border-border bg-white flex flex-col" data-testid={testId}>
      <div className={cn("flex items-center justify-between px-4 py-3 border-b border-border", accent)}>
        <div className="flex items-center gap-2 font-heading font-bold">
          <Icon size={17} /> {title}
        </div>
        <span className="font-mono text-sm font-bold">{items.length}</span>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">{empty}</div>
        ) : (
          items.map((c) => <FeedRow key={c.id} c={c} onClick={() => navigate(`/complaints/${c.id}`)} />)
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/dashboard").then((r) => setData(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (!data)
    return <div className="p-8 text-muted-foreground">Loading dashboard…</div>;

  const sc = data.status_counts;

  return (
    <div className="p-5 sm:p-8 fade-up">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-caps text-muted-foreground">Operations</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Dashboard</h1>
        </div>
        <button onClick={load} data-testid="dashboard-refresh" className="flex items-center gap-2 text-sm border border-border px-3 py-2 hover:bg-secondary transition-colors">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border mb-6">
        <StatCard label="Total" value={data.total} />
        <StatCard label="Open" value={data.open_count} accent="text-klein" />
        <StatCard label="Registered" value={sc.Registered} />
        <StatCard label="In Progress" value={sc["In Progress"]} />
        <StatCard label="Resolved" value={sc.Resolved} accent="text-emerald-600" />
        <StatCard label="Closed" value={sc.Closed} />
      </div>

      {/* Priority feeds */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <Feed title="Unassigned" icon={Inbox} items={data.feeds.unassigned}
          accent="bg-gray-50" testId="feed-unassigned" navigate={navigate}
          empty="Nothing waiting — all complaints assigned." />
        <Feed title="Assign Overdue (24h)" icon={Clock} items={data.feeds.assign_overdue}
          accent="bg-warn/20" testId="feed-assign-overdue" navigate={navigate}
          empty="No assignment SLA breaches." />
        <Feed title="Escalation · 72h Breach" icon={TriangleAlert} items={data.feeds.escalation}
          accent="bg-signal/10 text-signal" testId="feed-escalation" navigate={navigate}
          empty="No attend SLA breaches." />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Feed title="Recurring Complaints" icon={AlertTriangle} items={data.feeds.recurring}
          accent="bg-signal/10 text-signal" testId="feed-recurring" navigate={navigate}
          empty="No recurring complaints flagged." />
        <Feed title="Today's Scheduled Visits" icon={CalendarDays} items={data.feeds.scheduled_today}
          accent="bg-klein/5 text-klein" testId="feed-scheduled" navigate={navigate}
          empty="No visits scheduled today." />
      </div>
    </div>
  );
}
